import { useMemo, useState } from 'react';
import { Button } from '@/ui/Button';
import { InputSuelto } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { Categorias } from './Categorias';
import { Cumplimiento } from './Cumplimiento';
import { NuevoCurso } from './NuevoCurso';
import { IconoDePortada, PildorasDeFiltro, TIPOS, TarjetaDeCurso } from './piezas';
import { useCategorias, useCursos, type Curso } from './api';

/**
 * Cursos.tsx — el catálogo, `#cursos` y `#formacion`. Módulo 11, opción 1.
 *
 * ## Lo que esta pantalla no tenía: un estado de error
 *
 * El módulo entero tiene **65 bloques `catch` y cero estados de error con
 * reintento**, medido el 2026-09-18. Aquí el catálogo hacía esto al fallar:
 *
 *     Utils.emptyState('bi bi-wifi-off', 'Error cargando cursos',
 *                      'Verifica tu conexión e intenta de nuevo')
 *
 * Culpa a la conexión sin saberlo —puede ser un 403 o un 500— y **no ofrece
 * reintentar**: había que recargar el portal entero. `EstadoError` dice qué
 * pasó de verdad, distingue el 403 del 503 y trae su botón.
 *
 * Y las categorías iban con un `.catch(() => [])` pegado: si fallaban, los
 * filtros simplemente no aparecían. Ahora son su propia consulta.
 *
 * ## Los filtros viven en el cliente, y eso no cambia
 *
 * El endpoint acepta `course_type`, `category_id` y `level`, pero el vanilla
 * pedía 200 cursos una vez y filtraba en memoria. Se conserva: son los cursos de
 * una empresa, no un archivo histórico, y filtrar sin ir al servidor es lo que
 * hace que las píldoras se sientan instantáneas.
 */
export function Cursos() {
  // `cursos:manage` en el servidor. Comparar el rol por nombre le escondía los
  // botones a los roles personalizados que sí tienen el permiso.
  const puedeGestionar = Auth.can('cursos', 'manage') || Auth.isStaff();

  const cursos = useCursos();
  const categorias = useCategorias();

  const [tipo, setTipo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [creando, setCreando] = useState(false);
  const [viendoCategorias, setViendoCategorias] = useState(false);
  const [viendoCumplimiento, setViendoCumplimiento] = useState(false);

  const todos = useMemo(() => cursos.data ?? [], [cursos.data]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return todos.filter((c) => {
      if (tipo && c.course_type !== tipo) return false;
      if (categoria && String(c.category_id) !== categoria) return false;
      if (!q) return true;
      // Antes solo miraba el título: buscar «riesgo» no encontraba el curso
      // cuyo tema estaba en la descripción o en el objetivo.
      return [c.title, c.description, c.objective].some((t) => (t || '').toLowerCase().includes(q));
    });
  }, [todos, tipo, categoria, busqueda]);

  const sinFiltros = !tipo && !categoria && !busqueda.trim();
  const enProgreso = filtrados.filter((c) => c.enrolled && !c.completed && (c.progress_pct || 0) > 0);
  const continuar = [...enProgreso].sort((a, b) => (b.progress_pct || 0) - (a.progress_pct || 0))[0];

  const conUso = (categorias.data ?? []).filter((c) => (c.course_count || 0) > 0);

  const titulo = busqueda.trim()
    ? 'Resultados'
    : tipo
      ? (TIPOS[tipo]?.etiqueta ?? tipo)
      : (conUso.find((c) => String(c.id) === categoria)?.name ?? 'Todos los cursos');

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-mortarboard-fill" aria-hidden="true" />
            Cursos
          </h1>
          <p className="mt-1 text-3 text-muted">Formación y desarrollo del equipo</p>
        </div>
        {puedeGestionar && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setViendoCumplimiento(true)}>
              <i className="bi bi-shield-check" aria-hidden="true" /> Cumplimiento
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setViendoCategorias(true)}>
              <i className="bi bi-tags" aria-hidden="true" /> Categorías
            </Button>
            <Button size="sm" onClick={() => setCreando(true)}>
              <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nuevo curso
            </Button>
          </div>
        )}
      </header>

      {sinFiltros && continuar && <Continuar c={continuar} />}

      {!cursos.isLoading && !cursos.isError && <Resumen cursos={todos} />}

      <div className="mb-3 max-w-sm">
        <InputSuelto
          type="search"
          aria-label="Buscar cursos"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar cursos…"
        />
      </div>

      <PildorasDeFiltro
        etiqueta="Filtrar por tipo de curso"
        activa={tipo}
        onElegir={setTipo}
        opciones={[
          { valor: '', texto: 'Todos', icono: 'bi-grid-fill' },
          { valor: 'induccion', texto: 'Inducción', icono: TIPOS.induccion!.icono },
          { valor: 'curso', texto: 'Cursos', icono: TIPOS.curso!.icono },
          { valor: 'webinar', texto: 'Webinars', icono: TIPOS.webinar!.icono },
          { valor: 'taller', texto: 'Talleres', icono: TIPOS.taller!.icono },
        ]}
      />

      {/* Solo salen las categorías que tienen algún curso: una lista de
          etiquetas vacías es ruido que no filtra nada. */}
      {conUso.length > 0 && (
        <PildorasDeFiltro
          etiqueta="Filtrar por categoría"
          activa={categoria}
          onElegir={setCategoria}
          opciones={[
            { valor: '', texto: 'Todas las categorías', icono: 'bi-tags-fill' },
            ...conUso.map((c) => ({
              valor: String(c.id),
              texto: c.name,
              punto: c.color || undefined,
              cuenta: c.course_count,
            })),
          ]}
        />
      )}
      {categorias.isError && (
        <p className="mb-3 text-2 text-muted">
          No se pudieron cargar las categorías, así que su filtro no aparece. El catálogo sigue
          completo.
        </p>
      )}

      {sinFiltros && enProgreso.length > 0 && (
        <section aria-labelledby="cursos-en-progreso" className="mb-7">
          <h2 id="cursos-en-progreso" className="mb-3 flex items-center gap-2 text-4 font-bold text-brand-ink">
            <i className="bi bi-play-circle-fill" aria-hidden="true" /> En progreso
          </h2>
          <Rejilla cursos={enProgreso.slice(0, 4)} />
        </section>
      )}

      <section aria-labelledby="cursos-todos">
        <h2 id="cursos-todos" className="mb-3 flex items-center gap-2 text-4 font-bold text-brand-ink">
          <i className="bi bi-collection-fill" aria-hidden="true" /> {titulo}
          {!cursos.isLoading && !cursos.isError && (
            <span className="text-2 font-medium text-muted">({filtrados.length})</span>
          )}
        </h2>

        {cursos.isLoading ? (
          <SkeletonTarjetas n={8} />
        ) : cursos.isError ? (
          <EstadoError err={cursos.error} que="los cursos" onReintentar={() => void cursos.refetch()} />
        ) : !todos.length ? (
          <EstadoVacio
            icono="bi-mortarboard"
            titulo="Todavía no hay cursos"
            descripcion={
              puedeGestionar
                ? 'Crea el primero para que el equipo empiece a formarse.'
                : 'Cuando Bienestar publique uno, aparecerá aquí.'
            }
            accion={puedeGestionar ? { texto: 'Nuevo curso', onClick: () => setCreando(true) } : undefined}
          />
        ) : !filtrados.length ? (
          <EstadoVacio
            icono="bi-search"
            titulo="Sin resultados"
            descripcion="Prueba con otro filtro o con otras palabras."
            accion={{
              texto: 'Quitar los filtros',
              onClick: () => { setTipo(''); setCategoria(''); setBusqueda(''); },
            }}
            compacto
          />
        ) : (
          <Rejilla cursos={filtrados} />
        )}
      </section>

      {puedeGestionar && (
        <>
          <NuevoCurso abierto={creando} onCerrar={() => setCreando(false)} />
          <Categorias abierto={viendoCategorias} onCerrar={() => setViendoCategorias(false)} />
          <Cumplimiento abierto={viendoCumplimiento} onCerrar={() => setViendoCumplimiento(false)} />
        </>
      )}
    </>
  );
}

function Rejilla({ cursos }: { cursos: Curso[] }) {
  return (
    <ul className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(232px,1fr))]">
      {cursos.map((c) => (
        <li key={c.id} className="flex">
          <TarjetaDeCurso c={c} />
        </li>
      ))}
    </ul>
  );
}

/**
 * «Continúa aprendiendo»: el curso empezado con más avance.
 *
 * En el vanilla era un `<div onclick>` con cuatro capas decorativas encima
 * —dos «orbes», un degradado y una portada de fondo— y el botón real dentro,
 * llamando a `event.stopPropagation()`. Aquí es una tarjeta con **un solo
 * destino**: el enlace cubre la tarjeta y no hay nada que detener.
 */
function Continuar({ c }: { c: Curso }) {
  const pct = Math.round(c.progress_pct || 0);
  return (
    <section
      aria-labelledby="cursos-continuar"
      className="relative mb-5 overflow-hidden rounded-md border border-line bg-surface"
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div
          className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-sm max-sm:hidden"
          style={{ background: c.cover_icon_color || '#0D2B4E' }}
          aria-hidden="true"
        >
          {c.cover_url ? (
            <img src={c.cover_url} alt="" className="size-full object-cover" />
          ) : (
            <IconoDePortada valor={c.cover_icon} respaldo="bi-mortarboard" className="text-7 leading-none text-white" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p id="cursos-continuar" className="flex items-center gap-1.5 text-1 font-bold uppercase tracking-wide text-muted">
            <i className="bi bi-play-circle-fill" aria-hidden="true" /> Continúa aprendiendo
          </p>
          <h2 className="mt-0.5 text-5 font-bold leading-tight text-brand-ink [overflow-wrap:anywhere]">
            <a
              href={`#course-detail/${c.id}`}
              className="rounded-xs text-inherit no-underline after:absolute after:inset-0 focus-visible:focus-anillo"
            >
              {c.title}
            </a>
          </h2>
          <div className="mt-2 flex items-center gap-2">
            <div className="max-w-xs flex-1">
              <BarraDeProgreso pct={pct} etiqueta={`Avance de ${c.title}`} />
            </div>
            <span className="text-2 font-bold tabular-nums text-ink-2">{pct}%</span>
          </div>
        </div>

        {/* Decorativo y sin foco: el enlace del título ya cubre la tarjeta, así
            que un segundo control al mismo sitio sería una parada de Tab que no
            lleva a ninguna parte nueva. */}
        <span
          aria-hidden="true"
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-2 font-semibold text-on-brand max-sm:hidden"
        >
          <i className="bi bi-play-fill" /> Continuar
        </span>
      </div>
    </section>
  );
}

/** Cuatro números que resumen el catálogo. */
function Resumen({ cursos }: { cursos: Curso[] }) {
  const inscritos = cursos.filter((c) => c.enrolled).length;
  const hechos = cursos.filter((c) => c.completed || (c.progress_pct || 0) >= 100).length;
  const enCurso = cursos.filter((c) => c.enrolled && !c.completed && (c.progress_pct || 0) > 0).length;

  const filas: Array<[string, number, string]> = [
    ['bi-collection-fill', cursos.length, cursos.length === 1 ? 'curso' : 'cursos'],
    ['bi-bookmark-check-fill', inscritos, inscritos === 1 ? 'inscrito' : 'inscritos'],
    ['bi-play-circle-fill', enCurso, 'en progreso'],
    ['bi-check-circle-fill', hechos, hechos === 1 ? 'completado' : 'completados'],
  ];

  return (
    <ul className="mb-4 flex flex-wrap gap-2">
      {filas.map(([icono, n, etiqueta]) => (
        <li
          key={etiqueta}
          className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-2 text-ink-2"
        >
          <i className={`bi ${icono} text-brand-ink`} aria-hidden="true" />
          <span>
            <strong className="tabular-nums">{n}</strong> {etiqueta}
          </span>
        </li>
      ))}
    </ul>
  );
}
